import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { checkInventoryRecalculation, recalculateInventory, type ColumnNameValue, type InventoryRunSet } from '@/services/inventory/inventoryService';

type Tab = 'availability' | 'exceptions' | 'railcar';
interface Props {
  source: string;
  filters: ColumnNameValue[];
  tab: Tab;
  onClose: () => void;
  onSuccess: () => void;
  onEditFilters: () => void;
}

const SCOPE_COLUMNS: Record<Tab, string> = {
  availability: 'AvailabilityFlag', exceptions: 'ShowExceptionOnly', railcar: 'ShowRailcarOnly',
};

export function RecalculateInventoryDialog({ source, filters, tab, onClose, onSuccess, onEditFilters }: Props) {
  const [state, setState] = useState<'confirm' | 'running' | 'success' | 'failure'>('confirm');
  const [message, setMessage] = useState('');
  const [totalPairs, setTotalPairs] = useState<number>();
  const [canEditFilters, setCanEditFilters] = useState(false);
  const request = useRef<AbortController | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const busy = useRef(false);

  useEffect(() => () => {
    request.current?.abort();
    if (timer.current !== null) clearTimeout(timer.current);
  }, []);

  const start = async () => {
    if (busy.current) return;
    busy.current = true;
    const controller = new AbortController();
    request.current?.abort();
    request.current = controller;
    if (timer.current !== null) clearTimeout(timer.current);
    setState('running'); setMessage(''); setCanEditFilters(false); setTotalPairs(undefined);

    const fail = (detail: string, editable = false) => {
      busy.current = false;
      setMessage(detail); setCanEditFilters(editable); setState('failure');
    };
    const handleError = (error: unknown) => {
      if (controller.signal.aborted) return;
      const response = error as { code?: string; message?: string; response?: { status?: number; data?: string | { detail?: string; title?: string; errorMessage?: string } } };
      const data = response.response?.data;
      const detail = typeof data === 'string' ? data : data?.errorMessage ?? data?.detail ?? data?.title;
      const safeDetail = detail && !/<\s*(?:!doctype|html)/i.test(detail) ? detail.slice(0, 1000) : '';
      if (response.response?.status === 422) {
        fail(safeDetail || 'The maximum number of Material & Location pairs was exceeded. Narrow your filters and try again.', true);
      } else if (response.code === 'ECONNABORTED' || response.code === 'ETIMEDOUT' || /timeout|timed out/i.test(response.message ?? '') || response.response?.status === 504) {
        fail('Recalculations took too long, consider adding filters to reduce the number of pairs calculated.', true);
      } else {
        fail(safeDetail || 'An error prevented proper recalculations, try again.');
      }
    };
    const handleResult = (result: InventoryRunSet, previousId?: string | number) => {
      if (controller.signal.aborted) return;
      if (result.totalPairs != null) setTotalPairs(result.totalPairs);
      switch (result.status) {
        case 'Complete':
          busy.current = false; setState('success'); return;
        case 'Error':
          fail(result.errorMessage || 'An error prevented proper recalculations, try again.', true); return;
        case 'LockedOut':
          fail(result.errorMessage || 'Recalculation is locked for one or more supply points. Please try again later.'); return;
        case 'OvernightRecalculationRunning':
          fail(result.errorMessage || 'Overnight inventory recalculation is currently running. Please try again later.'); return;
      }
      const id = result.runSetId ?? result.id ?? previousId;
      if (id === undefined || id === null || id === '') {
        fail('The recalculation response did not include a run-set ID.'); return;
      }
      timer.current = setTimeout(() => {
        void checkInventoryRecalculation(id, controller.signal)
          .then((next) => handleResult(next, id)).catch(handleError);
      }, 10_000);
    };

    const scopeColumn = SCOPE_COLUMNS[tab];
    const scopedFilters = filters.some((filter) => filter.columnName === scopeColumn)
      ? filters.map((filter) => ({ ...filter }))
      : [...filters.map((filter) => ({ ...filter })), { columnName: scopeColumn, value: '1' }];
    try {
      handleResult(await recalculateInventory(source, scopedFilters, controller.signal));
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <Dialog open disableEscapeKeyDown maxWidth={false} className="recalculate-inventory-dialog" aria-labelledby="recalculate-inventory-title">
      <DialogTitle id="recalculate-inventory-title">
        {state === 'confirm' ? 'Recalculate inventory values' : state === 'running' ? 'Recalculating Table Values' : state === 'success' ? 'Recalculations successful' : 'Recalculations Failed'}
      </DialogTitle>
      <DialogContent>
        {state === 'confirm' && <>
          <Typography className="recalculate-confirm-message">
            {filters.length
              ? 'This will run calculations for all Material & Location pairs that match your current filters. Recalculating may take several minutes based on the number of records.'
              : 'This will run calculations for all Material & Location pairs. Recalculating may take hours based on the number of records, consider filtering your results for faster recalculation.'}
          </Typography>
          {filters.length > 0 && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 3 }}>
            {filters.map((filter) => <TextField key={filter.columnName} variant="standard" label={filter.columnName} value={filter.value} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />)}
          </Box>}
        </>}
        {state === 'running' && <Box role="status" aria-live="polite" sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={80} />
          <Typography sx={{ mt: 3 }}>This may take several minutes.</Typography>
        </Box>}
        {state === 'success' && <Alert severity="success">{totalPairs == null ? 'Inventory recalculation completed.' : `${totalPairs} Material & Location pairs recalculated`}</Alert>}
        {state === 'failure' && <Alert severity="error">{message}</Alert>}
      </DialogContent>
      {state !== 'running' && <DialogActions>
        {state !== 'success' && <Button onClick={onClose}>CANCEL</Button>}
        {state === 'confirm' && <Button variant="contained" onClick={() => { void start(); }}>RECALCULATE</Button>}
        {state === 'failure' && <>
          <Button onClick={() => { void start(); }}>TRY AGAIN</Button>
          {canEditFilters && <Button variant="contained" onClick={onEditFilters}>EDIT FILTERS</Button>}
        </>}
        {state === 'success' && <Button variant="contained" onClick={onSuccess}>OKAY</Button>}
      </DialogActions>}
    </Dialog>
  );
}